import { z } from 'zod';
import { AufgabeAbrechnung, AufgabeZustaendig, FaelligkeitStatus, Verbindlichkeit, } from '../enums.js';
/**
 * Aufgabe — eine wiederkehrende organisatorische Pflicht je Liegenschaft.
 *
 * Das Gegenstück zur Prüfung an der Anlage: Die Brandschutzordnung wird
 * fortgeschrieben, die Beschäftigten unterwiesen, die Räumung geübt. Der
 * Katalog liefert je Aufgabenart einen Rhythmus-Vorschlag mit Fundstelle;
 * die Liegenschaft übernimmt ihn oder weicht mit Begründung ab. Jede
 * Erledigung setzt die nächste Fälligkeit.
 *
 * `verbindlich` kommt aus der Betreuung, nicht aus dem Katalog: Dieselbe
 * Unterweisung ist Pflicht, wenn wir als Brandschutzbeauftragter bestellt
 * sind, und Empfehlung, wenn wir nur beraten.
 */
export const aufgabe = z.object({
    id: z.number().int().positive(),
    liegenschaftId: z.number().int().positive(),
    /** Schlüssel aus `aufgabenkatalog.json`; `null` bei einer freien Aufgabe. */
    katalogKey: z.string().nullable().optional(),
    titel: z.string(),
    /** `null` = nur bei Anlass fällig, keine automatische Folgefrist. */
    rhythmusMonate: z.number().int().nullable().optional(),
    /** Pflicht, wenn der Rhythmus vom Katalog abweicht. */
    rhythmusBegruendung: z.string().nullable().optional(),
    verbindlich: z.boolean(),
    naechsteFaelligkeit: z.string().datetime().nullable().optional(),
    zustaendig: AufgabeZustaendig,
    abrechnung: AufgabeAbrechnung.nullable().optional(),
    aktiv: z.boolean(),
    notiz: z.string().nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const createAufgabeRequest = aufgabe
    .omit({ id: true, liegenschaftId: true, aktiv: true, createdAt: true, updatedAt: true })
    .extend({
    katalogKey: z.string().trim().max(80).nullable().optional(),
    titel: z.string().trim().min(2).max(200),
    rhythmusMonate: z.number().int().positive().max(600).nullable().optional(),
    rhythmusBegruendung: z.string().trim().max(1000).nullable().optional(),
    verbindlich: z.boolean().default(false),
    zustaendig: AufgabeZustaendig.default('buero'),
    notiz: z.string().max(5000).nullable().optional(),
});
/** Der Katalogschlüssel ist beim Ändern raus — eine Aufgabe wechselt nicht ihre Art. */
export const updateAufgabeRequest = createAufgabeRequest
    .omit({ katalogKey: true })
    .partial()
    .extend({ aktiv: z.boolean().optional() });
export const aufgabeListQuery = z.object({
    /** Vorgabe: nur aktive. `false` zeigt auch deaktivierte. */
    nurAktive: z
        .string()
        .optional()
        .transform((v) => v !== 'false'),
    limit: z.coerce.number().int().positive().max(200).default(100),
    offset: z.coerce.number().int().nonnegative().default(0),
});
/**
 * Fälligkeitsstatus einer Aufgabe — derselbe wie bei den Prüfzyklen der
 * Anlagen (`FaelligkeitStatus` in enums.ts), damit die Oberfläche beide
 * Listen mit einer Pille zeigt.
 */
export const AufgabeFaelligkeitStatus = FaelligkeitStatus;
/** Aufgabe plus gerechneter Fälligkeit und Katalogkontext — so liefert die Liste sie. */
export const aufgabeZeile = aufgabe.extend({
    faelligkeitStatus: AufgabeFaelligkeitStatus,
    /** Negativ = überfällig. `null`, wenn keine Fälligkeit gesetzt ist. */
    tageBisFaellig: z.number().int().nullable(),
    letzteErledigungAm: z.string().datetime().nullable(),
    katalog: z
        .object({
        rhythmusMonate: z.number().int().nullable(),
        verbindlichkeit: Verbindlichkeit,
        grundlage: z.string(),
        quelleUrl: z.string(),
        belegt: z.boolean(),
        /** Gesetzt, wenn der Rhythmus der Aufgabe vom Katalog abweicht. */
        abweichend: z.boolean(),
    })
        .nullable(),
});
/**
 * Eine Erledigung — der Anker der nächsten Fälligkeit.
 *
 * `erledigtAm` darf nicht in der Zukunft liegen: Eine noch nicht erfolgte
 * Unterweisung als erledigt einzutragen, verschöbe die Frist ohne Nachweis.
 */
export const aufgabeErledigung = z.object({
    id: z.number().int().positive(),
    aufgabeId: z.number().int().positive(),
    erledigtAm: z.string().datetime(),
    durchUserId: z.number().int().positive().nullable().optional(),
    /** Wer es erledigt hat, als Freitext — bei Kunde oder Dienstleister. */
    durchText: z.string().nullable().optional(),
    nachweisDocumentId: z.number().int().positive().nullable().optional(),
    notiz: z.string().nullable().optional(),
    createdAt: z.string().datetime(),
});
export const createAufgabeErledigungRequest = aufgabeErledigung
    .omit({ id: true, aufgabeId: true, createdAt: true })
    .extend({
    durchText: z.string().trim().max(200).nullable().optional(),
    notiz: z.string().max(5000).nullable().optional(),
});
/**
 * Fälligkeitszeile einer Aufgabe für die zusammengeführte Fristenliste.
 *
 * Bewusst dieselben Felder wie die Anlagen-Fälligkeit dort, wo es die gleiche
 * Bedeutung gibt (`faelligAm`, `tageBisFaellig`), damit die Oberfläche beide
 * Listen ohne Sonderfälle nach Datum sortieren kann.
 */
export const aufgabeFaelligkeitZeile = z.object({
    aufgabeId: z.number().int().positive(),
    titel: z.string(),
    verbindlich: z.boolean(),
    zustaendig: AufgabeZustaendig,
    rhythmusMonate: z.number().int().nullable(),
    faelligAm: z.string().datetime().nullable(),
    tageBisFaellig: z.number().int().nullable(),
    faelligkeitStatus: AufgabeFaelligkeitStatus,
});
